/**
 * CertificationSelector — Premium certification switcher.
 *
 * A refined, production-grade selector that replaces the basic dropdown with a
 * floating command-panel aesthetic (inspired by Linear/Raycast). Features:
 *
 * - Provider monograms with distinct visual identity
 * - Floating panel with a solid Variant B surface and depth shadows
 * - Bottom sheet on mobile with swipe-to-dismiss
 * - Selection feedback with checkmark pulse + delayed close
 * - Full keyboard navigation and ARIA semantics
 * - Smooth theming transitions via CSS @property
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useCertStore } from '../stores/certStore';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchCertification } from '../api/certifications';
import { useMediaQuery } from '../hooks/useMediaQuery';
import ProviderMonogram from './ProviderMonogram';
import type { CertificationSummaryOut, CertificationDetail } from '../types';

/* ── Types ──────────────────────────────────────────────────────────────── */

interface ProviderGroup {
  providerCode: string;
  providerName: string;
  providerColor: string;
  items: CertificationSummaryOut[];
}

type PanelState = 'closed' | 'opening' | 'open' | 'closing';

/* ── Helpers ────────────────────────────────────────────────────────────── */

function groupByProvider(certs: CertificationSummaryOut[]): ProviderGroup[] {
  const map = new Map<string, ProviderGroup>();
  for (const cert of certs) {
    const existing = map.get(cert.provider.code);
    if (existing) {
      existing.items.push(cert);
    } else {
      map.set(cert.provider.code, {
        providerCode: cert.provider.code,
        providerName: cert.provider.name,
        providerColor: cert.provider.color,
        items: [cert],
      });
    }
  }
  return Array.from(map.values());
}

function flatCodes(groups: ProviderGroup[]): string[] {
  return groups.flatMap((g) => g.items.map((c) => c.code));
}

/* ── Chevron ────────────────────────────────────────────────────────────── */

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-3.5 w-3.5 text-slate-400 dark:text-slate-500 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
      style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

/* ── Metadata Pill ──────────────────────────────────────────────────────── */

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md bg-slate-100/80 px-1.5 py-[2px] text-[10px] font-semibold tabular-nums text-slate-500 dark:bg-white/[0.06] dark:text-slate-400">
      {children}
    </span>
  );
}

/* ── Certification Card ─────────────────────────────────────────────────── */

interface CertCardProps {
  cert: CertificationSummaryOut;
  detail: CertificationDetail | undefined;
  isSelected: boolean;
  justSelected: boolean;
  isFocused: boolean;
  providerColor: string;
  mobile: boolean;
  onSelect: (code: string) => void;
  onFocus: () => void;
}

function CertCard({
  cert, detail, isSelected, justSelected, isFocused, providerColor, mobile, onSelect, onFocus,
}: CertCardProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement>(null);
  const isComingSoon = cert.is_active === false;

  useEffect(() => {
    if (isFocused && ref.current) {
      ref.current.focus({ preventScroll: false });
      ref.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isFocused]);

  return (
    <button
      ref={ref}
      role="option"
      aria-selected={isSelected}
      aria-disabled={isComingSoon || undefined}
      onClick={() => !isComingSoon && onSelect(cert.code)}
      onFocus={onFocus}
      disabled={isComingSoon}
      className={`group/card flex w-full items-center text-left transition-all duration-150 ease-out focus-visible:outline-none
        ${mobile ? 'mx-0 mb-1.5 rounded-2xl px-4 py-3.5 min-h-[56px]' : 'mx-0 mb-0.5 rounded-xl px-3 py-2.5 min-h-[44px]'}
        ${isComingSoon
          ? 'opacity-50 cursor-not-allowed'
          : isSelected
            ? 'ring-2 ring-primary'
            : 'hover:translate-x-[2px]'
        }
      `}
      style={{
        backgroundColor: isSelected && !isComingSoon
          ? providerColor + '0F'
          : undefined,
        '--tw-ring-color': isSelected ? providerColor + '22' : 'transparent',
      } as React.CSSProperties}
    >
      {/* Accent bar */}
      <div
        className={`mr-3 w-[3px] self-stretch rounded-full transition-all duration-200 ${isComingSoon ? 'opacity-20' : isSelected ? 'opacity-100' : 'opacity-35'}`}
        style={{ backgroundColor: providerColor }}
        aria-hidden="true"
      />

      {/* Text */}
      <div className="flex min-w-0 flex-1 flex-col">
        <span
          className={`truncate text-[13px] font-semibold transition-colors duration-150 ${isComingSoon ? 'text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-100'}`}
          style={{ color: isSelected && !isComingSoon ? providerColor : undefined }}
        >
          {cert.name}
        </span>
        <span className="text-[11px] text-slate-400 dark:text-slate-500 font-medium">
          {cert.provider.name} &middot; {cert.code.toUpperCase()}
        </span>
      </div>

      {/* Coming Soon badge */}
      {isComingSoon && (
        <span className="ml-2 flex-shrink-0 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
          {t('components.certificationSelector.comingSoon')}
        </span>
      )}

      {/* Metadata pills — desktop only, active certs */}
      {detail && !mobile && !isComingSoon && (
        <div className="ml-2 flex items-center gap-1 flex-shrink-0">
          <Pill>{detail.exam_question_count}Q</Pill>
          <Pill>{detail.exam_duration_minutes}m</Pill>
          <Pill>{detail.passing_score_percent}%</Pill>
        </div>
      )}

      {/* Checkmark */}
      {isSelected && !isComingSoon && (
        <span
          className={`ml-2 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full shadow-sm ${justSelected ? 'animate-check-pulse' : ''}`}
          style={{ backgroundColor: providerColor }}
        >
          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </span>
      )}

      {/* Hover indicator — subtle arrow (active certs only) */}
      {!isSelected && !isComingSoon && (
        <svg
          className="ml-2 h-3.5 w-3.5 flex-shrink-0 text-slate-300 dark:text-slate-600 opacity-0 -translate-x-1 group-hover/card:opacity-100 group-hover/card:translate-x-0 transition-all duration-150"
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      )}
    </button>
  );
}

/* ── Provider Group Header ──────────────────────────────────────────────── */

function GroupHeader({ group }: { group: ProviderGroup }) {
  return (
    <div className="flex items-center gap-2 px-3 pt-3 pb-1.5">
      <div className="scale-[0.65] origin-left">
        <ProviderMonogram providerCode={group.providerCode} size="sm" animated={false} />
      </div>
      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 dark:text-slate-500">
        {group.providerName}
      </span>
      <span className="ml-auto rounded-full bg-slate-100 dark:bg-white/[0.06] px-1.5 py-[1px] text-[9px] font-bold text-slate-400 dark:text-slate-500 tabular-nums">
        {group.items.length}
      </span>
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────────────────── */

export default function CertificationSelector() {
  const { t } = useTranslation();
  const [panelState, setPanelState] = useState<PanelState>('closed');
  const [justSelectedCode, setJustSelectedCode] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);
  const touchCurrentY = useRef(0);

  const isMobile = useMediaQuery('(max-width: 639px)');

  /* Store */
  const certCode = useCertStore((s) => s.certCode);
  const certName = useCertStore((s) => s.certName);
  const providerCode = useCertStore((s) => s.providerCode);
  const providerColor = useCertStore((s) => s.providerColor);
  const isLoading = useCertStore((s) => s.isLoading);
  const certifications = useCertStore((s) => s._certifications);
  const setCertification = useCertStore((s) => s.setCertification);

  const allCerts = certifications;
  const groups = useMemo(() => groupByProvider(allCerts), [allCerts]);
  const activeCodes = useMemo(() => flatCodes(groups).filter((code) => {
    const cert = allCerts.find((c) => c.code === code);
    return cert?.is_active !== false;
  }), [groups, allCerts]);
  const allCodes = activeCodes; // keyboard nav only on active certs

  /* Pre-fetch details for metadata pills */
  const detailQueriesConfig = useMemo(() =>
    allCerts.map((cert) => ({
      queryKey: ['certification', cert.code],
      queryFn: () => fetchCertification(cert.code),
      staleTime: 10 * 60 * 1000,
      enabled: allCerts.length > 0,
    })),
    [allCerts]
  );
  const detailQueries = useQueries({ queries: detailQueriesConfig });

  const detailMap = useMemo(() => {
    const map = new Map<string, CertificationDetail>();
    detailQueries.forEach((q, i) => {
      if (q.data) map.set(allCerts[i].code, q.data);
    });
    return map;
  }, [detailQueries, allCerts]);

  /* ── Open / Close ────────────────────────────────────────────────── */

  const isOpen = panelState === 'opening' || panelState === 'open';

  const openPanel = useCallback(() => {
    setPanelState('opening');
    setFocusedIndex(-1);
    requestAnimationFrame(() => setPanelState('open'));
  }, []);

  const closePanel = useCallback(() => {
    setPanelState('closing');
    setTimeout(() => {
      setPanelState('closed');
      triggerRef.current?.focus();
    }, isMobile ? 200 : 150);
  }, [isMobile]);

  const toggle = useCallback(() => {
    if (isOpen) {
      closePanel();
    } else {
      openPanel();
    }
  }, [isOpen, openPanel, closePanel]);

  /* ── Selection ───────────────────────────────────────────────────── */

  const handleSelect = useCallback((code: string) => {
    setJustSelectedCode(code);
    setCertification(code);
    setTimeout(() => {
      closePanel();
      setJustSelectedCode(null);
    }, 280);
  }, [setCertification, closePanel]);

  /* ── Outside click ───────────────────────────────────────────────── */

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (isMobile) {
        if (sheetRef.current && !sheetRef.current.contains(t)) closePanel();
      } else {
        if (containerRef.current && !containerRef.current.contains(t)) closePanel();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, closePanel, isMobile]);

  /* ── Escape ──────────────────────────────────────────────────────── */

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); closePanel(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closePanel]);

  /* ── Keyboard nav ────────────────────────────────────────────────── */

  const handleTriggerKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!isOpen) openPanel(); setFocusedIndex(0); }
    if (e.key === 'ArrowUp') { e.preventDefault(); if (!isOpen) openPanel(); setFocusedIndex(allCodes.length - 1); }
  };

  const handlePanelKey = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setFocusedIndex((i) => (i + 1) % allCodes.length); break;
      case 'ArrowUp': e.preventDefault(); setFocusedIndex((i) => (i - 1 + allCodes.length) % allCodes.length); break;
      case 'Enter': case ' ': e.preventDefault(); if (focusedIndex >= 0) handleSelect(allCodes[focusedIndex]); break;
      case 'Home': e.preventDefault(); setFocusedIndex(0); break;
      case 'End': e.preventDefault(); setFocusedIndex(allCodes.length - 1); break;
    }
  };

  /* ── Touch gestures (bottom sheet) ───────────────────────────────── */

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    if (sheetRef.current) sheetRef.current.style.transition = 'none';
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta > 0 && sheetRef.current) {
      touchCurrentY.current = delta;
      sheetRef.current.style.transform = `translateY(${delta}px)`;
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!sheetRef.current) return;
    sheetRef.current.style.transition = 'transform 200ms ease-out';
    if (touchCurrentY.current > 100) {
      sheetRef.current.style.transform = 'translateY(100%)';
      setTimeout(closePanel, 200);
    } else {
      sheetRef.current.style.transform = 'translateY(0)';
    }
    touchCurrentY.current = 0;
  }, [closePanel]);

  /* ── Lock scroll on mobile sheet ─────────────────────────────────── */

  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isMobile, isOpen]);

  /* ── Trigger rect for portal positioning ─────────────────────────── */

  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      setTriggerRect(triggerRef.current.getBoundingClientRect());
    }
  }, [isOpen]);

  /* ── Loading skeleton ────────────────────────────────────────────── */

  if (isLoading || certifications.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-xl pl-1 pr-3 py-1" aria-busy="true">
        <div className="h-8 w-8 animate-pulse rounded-[10px] bg-slate-200 dark:bg-white/10" />
        <div className="hidden sm:block h-3 w-20 animate-pulse rounded-md bg-slate-200 dark:bg-white/10" />
      </div>
    );
  }

  /* ── Shared panel content ────────────────────────────────────────── */

  const content = (mobile: boolean) => (
    <>
      {/* Header */}
      <div className={`${mobile ? 'px-5 pt-2 pb-1' : 'px-4 pt-3.5 pb-1'}`}>
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500">
          {t('components.certificationSelector.switchHeading')}
        </span>
      </div>

      {/* Divider */}
      <div className={`${mobile ? 'mx-5' : 'mx-4'} h-px bg-slate-100 dark:bg-white/[0.06] mb-1`} />

      {/* Groups */}
      <div className={mobile ? 'px-2' : 'px-1.5'}>
        {groups.map((group) => (
          <div key={group.providerCode}>
            <GroupHeader group={group} />
            {group.items.map((cert) => {
              const idx = allCodes.indexOf(cert.code);
              return (
                <CertCard
                  key={cert.code}
                  cert={cert}
                  detail={detailMap.get(cert.code)}
                  isSelected={cert.code === certCode}
                  justSelected={cert.code === justSelectedCode}
                  isFocused={idx === focusedIndex}
                  providerColor={group.providerColor}
                  mobile={mobile}
                  onSelect={handleSelect}
                  onFocus={() => setFocusedIndex(idx)}
                />
              );
            })}
          </div>
        ))}
      </div>

      <div className="h-2" />
    </>
  );

  /* ── Desktop panel (portaled) ──────────────────────────────────── */

  const desktopPanel = !isMobile && panelState !== 'closed' && createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 transition-opacity duration-200 ${
          panelState === 'closing' ? 'opacity-0' : 'bg-black/[0.03] dark:bg-black/20 backdrop-blur-[2px]'
        }`}
        onClick={closePanel}
        aria-hidden="true"
      />
      {/* Panel */}
      <div
        ref={panelRef}
        role="listbox"
        aria-label={t('components.certificationSelector.ariaListLabel')}
        tabIndex={-1}
        onKeyDown={handlePanelKey}
        className={`cert-panel absolute w-[340px] overflow-y-auto rounded-2xl border border-slate-200/70 bg-white dark:border-white/10 dark:bg-card-dark
          shadow-[0_24px_80px_-12px_rgba(0,0,0,0.4),0_8px_24px_-8px_rgba(0,0,0,0.3)]
          ${panelState === 'closing' ? 'animate-cert-panel-exit' : 'animate-cert-panel-enter'}`}
        style={{
          maxHeight: 'min(480px, calc(100vh - 120px))',
          top: triggerRect ? triggerRect.bottom + 8 : 64,
          left: triggerRect ? triggerRect.left : 0,
        }}
      >
        {content(false)}
      </div>
    </div>,
    document.body,
  );

  /* ── Mobile bottom sheet (portaled) ────────────────────────────── */

  const mobileSheet = isMobile && panelState !== 'closed' && createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 transition-opacity duration-200 ${
          panelState === 'closing' ? 'opacity-0' : 'bg-black/25 dark:bg-black/50 backdrop-blur-sm'
        }`}
        onClick={closePanel}
        aria-hidden="true"
      />
      {/* Sheet */}
      <div
        ref={sheetRef}
        role="listbox"
        aria-label={t('components.certificationSelector.ariaListLabel')}
        tabIndex={-1}
        onKeyDown={handlePanelKey}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className={`cert-panel absolute inset-x-0 bottom-0 overflow-y-auto rounded-t-[28px]
          border-t border-slate-200/70 bg-white dark:border-white/10 dark:bg-card-dark will-change-transform
          shadow-[0_-16px_48px_rgba(0,0,0,0.4)]
          ${panelState === 'closing' ? 'animate-sheet-exit' : 'animate-sheet-enter'}`}
        style={{ maxHeight: '72vh', paddingBottom: 'env(safe-area-inset-bottom, 20px)' }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="h-[5px] w-10 rounded-full bg-slate-300/80 dark:bg-slate-600" />
        </div>
        {content(true)}
      </div>
    </div>,
    document.body,
  );

  /* ── Trigger + panels ────────────────────────────────────────────── */

  return (
    <div className="relative" ref={containerRef} aria-label={t('components.certificationSelector.label')}>
      <button
        ref={triggerRef}
        onClick={toggle}
        onKeyDown={handleTriggerKey}
        className="cert-trigger group flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white pl-1 pr-2.5 py-1 dark:border-white/10 dark:bg-white/5
          transition-all duration-200 ease-out
          hover:scale-[1.02] active:scale-[0.98]
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        style={{ '--tw-ring-color': providerColor + '66' } as React.CSSProperties}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('components.certificationSelector.currentAria', { cert: certName || certCode })}
      >
        <ProviderMonogram providerCode={providerCode} size="sm" />

        <span className="hidden sm:inline max-w-[160px] truncate text-[13px] font-semibold text-slate-800 dark:text-slate-100">
          {certName || certCode}
        </span>
        <span className="sm:hidden text-[13px] font-semibold text-slate-700 dark:text-slate-200">
          {certCode.toUpperCase().replace('-', '')}
        </span>

        <Chevron open={isOpen} />
      </button>

      {desktopPanel}
      {mobileSheet}
    </div>
  );
}
